import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import "./PersonalInfo.css";
import "./utilities.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${API_BASE_URL}/api/user`;

function PersonalInfo() {
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Track if the mobile number was already set in the database
  const [isMobileLocked, setIsMobileLocked] = useState(false);

  const [user, setUser] = useState({
    name: "",
    email: "",
    mobile: "",
    gender: "",
    dob: "",
  });

  useEffect(() => {
    getProfile();
   
  }, []);

  const getProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = res.data?.user || res.data;

      // Checking both 'mobile' and 'phone' just in case your backend uses 'phone'
      const userMobile = data.mobile || data.phone || "";

      setUser({
        name: data.name || "",
        email: data.email || "",
        mobile: userMobile,
        gender: data.gender || "",
        dob: data.dob ? data.dob.split("T")[0] : "",
      });

      // If a mobile number already exists, lock the field
      if (userMobile.trim() !== "") {
        setIsMobileLocked(true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Unable to load profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setUser({
      ...user,
      [e.target.name]: e.target.value,
    });
  };

  const saveProfile = async () => {
    if (!user.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!isMobileLocked && user.mobile && !/^[6-9]\d{9}$/.test(user.mobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      setSaving(true);

      // 🚨 CRITICAL FIX: We MUST include email in the payload 
      // otherwise the backend might reject the update!
      const payload = {
        name: user.name,
        email: user.email, 
        gender: user.gender,
        dob: user.dob,
      };

      // Only send the mobile number if it is being set for the first time
      if (!isMobileLocked && user.mobile) {
        payload.mobile = user.mobile;
        payload.phone = user.mobile; // Sending both to ensure the backend catches it
      }

      await axios.put(`${API}/profile`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Profile updated successfully.");

      // 🚀 UPDATE LOCAL STORAGE: This ensures your Header and Sidebar update instantly!
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({
        ...storedUser,
        name: user.name,
        mobile: user.mobile
      }));

      // Lock the mobile field immediately if it was just saved
      if (user.mobile && user.mobile.trim() !== "") {
        setIsMobileLocked(true);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Unable to update profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-card loading-card">
        <div className="loader"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-card">
      {/* Header */}
      <div className="card-title">
        <div>
          <h2>Personal Information</h2>
          <p>Update your personal information and contact details.</p>
        </div>
      </div>

      {/* Profile Summary */}
      <div className="profile-summary">
        <div className="profile-avatar">
          {user.name ? user.name.charAt(0).toUpperCase() : "U"}
        </div>
        <div className="profile-details">
          <h3>{user.name}</h3>
          <p>{user.email}</p>
          <span>{user.mobile || "Add your mobile number"}</span>
        </div>
        <div className="profile-status">
          <i className="fas fa-circle-check"></i>
          Verified Account
        </div>
      </div>

      {/* Form */}
      <div className="profile-grid">
        <div className="input-group">
          <label>
            <i className="fas fa-user"></i> Full Name
          </label>
          <input
            type="text"
            name="name"
            value={user.name}
            onChange={handleChange}
            placeholder="Enter your full name"
          />
        </div>

        <div className="input-group">
          <label>
            <i className="fas fa-phone"></i> Mobile Number
          </label>
          <input
            type="text"
            name="mobile"
            value={user.mobile}
            onChange={handleChange}
            readOnly={isMobileLocked}
            disabled={isMobileLocked}
            maxLength={10}
            placeholder="Enter 10-digit mobile number"
            title={isMobileLocked ? "Mobile number cannot be changed once set." : ""}
          />
        </div>

        <div className="input-group">
          <label>
            <i className="fas fa-envelope"></i> Email Address
          </label>
          <input
            type="email"
            name="email"
            value={user.email}
            readOnly
            disabled
            title="Email address cannot be changed."
          />
        </div>

        <div className="input-group">
          <label>
            <i className="fas fa-venus-mars"></i> Gender
          </label>
          <select name="gender" value={user.gender} onChange={handleChange}>
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="input-group full-width">
          <label>
            <i className="fas fa-calendar"></i> Date of Birth
          </label>
          <input
            type="date"
            name="dob"
            value={user.dob}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="profile-actions">
        <button
          className="btn-outline"
          type="button"
          onClick={getProfile}
          disabled={saving}
        >
          Cancel
        </button>

        <button
          className="btn-primary"
          onClick={saveProfile}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-floppy-disk"></i> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default PersonalInfo;